/**
 * Tech-debt #9 — standalone helpers extracted from useStationStore.ts. These
 * are pure or repo-only (no closure over the store's set/get), so they live
 * outside the create() body. The store-singleton-coupled helpers
 * (advanceSocForConnector / hydrateConnectorState) stay in the store for now.
 */
import type { EVSEKind } from '@/domain/EVSE';
import { lookup as lookupAuthList } from '@/domain/LocalAuthList';
import { ConnectionState, StationId, StopReason, type AuthStatus } from '@/domain/types';
import { DEFAULT_SAMPLED_DATA, KNOWN_MEASURANDS } from '@/protocol/v16/actions/meterValues';
import { useConfigurationStore } from '../useConfigurationStore';
import type { StationStoreState } from '../useStationStore';
import { configRepo, localAuthListRepo } from './runtime';

/**
 * Read the live `MeterValuesSampledData` CSL from the configuration store and
 * reduce it to a Set the wire builder can probe. Falls back to the canonical
 * baseline (Energy register only) when the key is missing or blank.
 */
export function readMeasurandSet(): ReadonlySet<string> {
  const raw =
    useConfigurationStore
      .getState()
      .entries.find((e) => e.key === 'MeterValuesSampledData')?.value ?? DEFAULT_SAMPLED_DATA;
  const out = new Set<string>();
  for (const piece of raw.split(',')) {
    const trimmed = piece.trim();
    if (KNOWN_MEASURANDS.has(trimmed)) out.add(trimmed);
  }
  if (out.size === 0) out.add('Energy.Active.Import.Register');
  return out;
}

/**
 * FEAT-011 — legacy name-based heuristic, kept only as a fallback for
 * topologies persisted before the explicit `kind` field existed. Matches the
 * default topology naming ("EVSE 2 (DC)"): anything containing "DC"
 * (case-insensitive) is DC; everything else is AC. New code should prefer
 * `connectorTypeForEvse`, which honours the user's explicit selection.
 */
export function inferConnectorType(evseName: string | undefined): EVSEKind {
  if (evseName === undefined) return 'AC';
  return /\bdc\b/i.test(evseName) ? 'DC' : 'AC';
}

/**
 * FEAT-011 — single source of truth for a connector's AC/DC type. Honours the
 * EVSE's explicit `kind` selection; falls back to the legacy name heuristic
 * only when `kind` is absent (old persisted topologies). Pass the whole
 * `EvseState` (or any `{ kind?, name }`) — never re-derive from the name alone.
 */
export function connectorTypeForEvse(
  evse: { kind?: EVSEKind; name: string } | undefined,
): EVSEKind {
  return evse?.kind ?? inferConnectorType(evse?.name);
}

export function emptyState(): StationStoreState {
  return {
    stationId: StationId.of('00000000-0000-4000-8000-000000000000'),
    cpid: '',
    wsUrl: '',
    displayName: null,
    ocppVersion: '1.6J',
    connectionState: ConnectionState.Disconnected,
    cpMaxPowerW: 0,
    evses: [],
    connectors: new Map(),
    authBanner: null,
    lastError: null,
  };
}

export function parseStopReason(raw: string): StopReason {
  const known = Object.values(StopReason) as readonly string[];
  return known.includes(raw) ? (raw as StopReason) : StopReason.Local;
}

/**
 * SPEC-001 outbound side — local-first authorization (OCPP 1.6J §9.1).
 *
 * Gated first by `LocalAuthListEnabled` (master feature switch). The second
 * gate depends on the CP's connection state, matching the spec's two distinct
 * keys:
 *   - ONLINE  → `LocalPreAuthorize` must be 'true' (authorize from the local
 *               list/cache BEFORE round-tripping to the CSMS).
 *   - OFFLINE → `LocalAuthorizeOffline` must be 'true' (authorize from the
 *               local list while disconnected).
 *
 * When the relevant gate passes AND the idTag hits the local list, returns the
 * cached AuthStatus so the caller can skip the CSMS Authorize CALL. Returns
 * null on a closed gate or a list miss. Expired entries surface as `Expired`
 * rather than falling through.
 */
export async function tryLocalAuthorize(
  stationId: StationId,
  idTag: string,
  opts: { readonly online: boolean },
): Promise<AuthStatus | null> {
  const gateKey = opts.online ? 'LocalPreAuthorize' : 'LocalAuthorizeOffline';
  const [enabledRow, gateRow] = await Promise.all([
    configRepo.get(stationId, 'LocalAuthListEnabled'),
    configRepo.get(stationId, gateKey),
  ]);
  if (enabledRow?.value !== 'true' || gateRow?.value !== 'true') {
    return null;
  }
  const rows = await localAuthListRepo.byStation(stationId);
  const hit = lookupAuthList(
    {
      listVersion: 0, // listVersion not needed for lookup
      entries: rows.map((r) => ({
        idTag: r.idTag,
        status: r.status,
        ...(r.expiryDate !== undefined ? { expiryDate: r.expiryDate } : {}),
        ...(r.parentIdTag !== undefined ? { parentIdTag: r.parentIdTag } : {}),
      })),
    },
    idTag,
  );
  if (hit.kind === 'miss') return null;
  return hit.expired ? 'Expired' : hit.entry.status;
}
